import { Column, Columns, EmptyState, FlexBox } from '@dixa/lib-shared-habla';
import produce from 'immer';
import { ampli } from 'lib-shared-tracking';
import type {
  AdvancedSearchResultScope,
  SearchParameters,
} from 'lib-shared-types-api';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';

import { searchApi } from 'apis/search';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import { SearchFilters } from './components/filters/searchFilters';
import { SearchFiltersProvider } from './components/filters/searchFilters.context';
import { filterFieldsPerTab } from './components/filters/utils/definitions';
import { SearchInput } from './components/input/searchInput';
import { SearchResultsLoading } from './components/results/components/loading/searchResultsLoading';
import { SearchResults } from './components/results/searchResults';
import { SavedSearchButton } from './components/savedSearch/button/savedSearchButton';
import { SavedSearchFormModal } from './components/savedSearch/form/savedSearchFormModal';
import { SavedSearchListModal } from './components/savedSearch/list/savedSearchListModal';
import { SearchTabs } from './components/tabs/searchTabs';
import { searchActions } from './search.actions';
import styles from './search.view.module.scss';
import { createSearchUrlParams } from './utils/url/createSearchUrl';
import { useSearchInit } from './utils/useSearchInit/useSearchInit';

export const SearchView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();

  const { isInitialized } = useSearchInit();
  const { scope } = useAppPathParams('/search/:scope');
  const { params } = useAppSelector((state) => state.search);

  const [readSearchResultsQuery, { isLoading, isError }] =
    searchApi.useLazyReadAdvancedSearchResultsQuery();
  const readSearchResultsQueryResultRef =
    useRef<ReturnType<typeof readSearchResultsQuery>>();

  const readSearchResults = useCallback(() => {
    if (readSearchResultsQueryResultRef.current) {
      readSearchResultsQueryResultRef.current.abort();
    }

    readSearchResultsQueryResultRef.current = readSearchResultsQuery({
      params: {
        ...params,
        scope,
      },
    });
  }, [params, readSearchResultsQuery, scope]);

  useEffect(() => {
    if (isInitialized) {
      readSearchResults();
    }
  }, [isInitialized, readSearchResults]);

  const updateSearchParams = useCallback(
    (newParams: Partial<SearchParameters>) => {
      dispatch(searchActions.params.update({ ...newParams, page: 1 }));
    },
    [dispatch],
  );

  const handleChangeScope = useCallback(
    (newScope: AdvancedSearchResultScope) => {
      if (scope === newScope) return;

      const nextParams = produce(params, (draft) => {
        draft.scope = newScope;

        draft.filters.conditions = draft.filters.conditions.filter(
          (condition) =>
            filterFieldsPerTab[draft.scope].includes(condition.field),
        );
      });

      updateSearchParams(nextParams);
      navigate({
        path: '/search/:scope',
        params: createSearchUrlParams(nextParams),
      });

      ampli.searchTabOpened({ tab: newScope });
    },
    [navigate, params, scope, updateSearchParams],
  );

  const handleSubmitInput = useCallback(
    (query: string, strict: boolean) => {
      const nextParams: Partial<SearchParameters> = { query, strict };

      if (!query) {
        nextParams.strict = false;
      }
      updateSearchParams(nextParams);

      ampli.searchFieldSubmitted({
        location: 'searchPage',
        isEmpty: !query,
        query,
      });
    },
    [updateSearchParams],
  );

  const handleClearInput = useCallback(() => {
    updateSearchParams({ query: '', strict: false });

    if (!params.filters.conditions.length) {
      dispatch(searchActions.savedSearch.close());
    }

    ampli.searchFieldCleared({ location: 'searchPage' });
  }, [dispatch, params.filters.conditions.length, updateSearchParams]);

  const handleClearSavedSearch = useCallback(() => {
    dispatch(searchActions.savedSearch.close());

    navigate({
      path: '/search/:scope',
      params: createSearchUrlParams({ scope }),
    });

    ampli.savedSearchClosed();
  }, [dispatch, navigate, scope]);

  if (!scope || !['conversations', 'contacts'].includes(scope)) {
    return <Navigate to="/search/conversations" replace />;
  }

  const renderContent = (): ReactNode => {
    if (!isInitialized || isLoading) {
      return <SearchResultsLoading />;
    }

    if (isError) {
      return (
        <FlexBox
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <EmptyState
            title="Something went wrong"
            description="We're experiencing some issues, try again or reload the page. If this issue persists, please contact support."
            icon="exclamation"
            iconColor="red.500"
            buttonProps={{
              children: 'Try again',
              onClick: readSearchResults,
            }}
          />
        </FlexBox>
      );
    }

    return <SearchResults />;
  };

  return (
    <Columns>
      <Column>
        <Column.Row className={styles.inputRow} noMinHeight>
          <SearchInput
            onSearch={handleSubmitInput}
            onClear={handleClearInput}
            onSavedSearchClear={handleClearSavedSearch}
          />
        </Column.Row>

        <Column.Row
          slottable
          borderBottom
          noMinHeight
          noVerticalPadding
          className={styles.tabsRow}
        >
          <Column.Slot placement="start" className={styles.tabsSlot}>
            <SearchTabs onChange={handleChangeScope} />
          </Column.Slot>

          <Column.Slot placement="end">
            <FlexBox gap="200">
              <SearchFiltersProvider>
                <SearchFilters />
              </SearchFiltersProvider>

              <SavedSearchButton
                buttonProps={{
                  variant: 'secondary',
                }}
              >
                Saved Searches
              </SavedSearchButton>
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row size={1} noVerticalPadding noHorizontalPadding>
          {renderContent()}
        </Column.Row>

        <SavedSearchFormModal />
        <SavedSearchListModal />
      </Column>
    </Columns>
  );
};
